/**
 * Unified Chat Hook — Single SSE hook for all CEPI chat modes.
 *
 * Replaces: useDrugQA, useCompareQA, useQAStream
 *
 * Handles:
 * - SSE streaming (ReadableStream pattern)
 * - Message state management
 * - Conversation history
 * - One conversation per identity (shell store), kept across context changes
 * - Abort/cancel
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import { parseCapabilityAction } from '../services/parseCapabilityAction';
import {
  buildChatScope,
  readShell,
  trimToRecentTurns,
  writeShellChat,
  writeShellCorpusPinned,
} from '../utils/chatHistoryStore';
import type { ShellSnapshot } from '../utils/chatHistoryStore';
import type {
  CapabilityAction,
  QAMessage,
  SimpleCitation,
  SegmentVerification,
} from '../types/qa';

// SSE streaming requires the Gen 2 Cloud Function (apiStreamProxy): the Gen 1
// apiProxy buffers the entire response body, so status/tool_use progress
// events would only arrive with the final answer. Same fallback chain as
// useStreamingSearch.
const STREAMING_API_URL = import.meta.env['VITE_STREAMING_API_URL'] ||
  import.meta.env.VITE_API_URL ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

// =============================================================================
// Types
// =============================================================================

export type ChatMode =
  | 'drug_qa'
  | 'drug_compare'
  | 'search_results'
  | 'search_results_letters'
  | 'regulatory_document'
  | 'fda_letter';

/**
 * Family discriminator for the regulatory_document chat mode.  Mirrors
 * the backend Family enum (regulatory_family.SBA_FAMILY / EPAR_FAMILY /
 * UK_PIL_FAMILY).  Adding a new family is a one-line change here when
 * the backend grows one — no per-family duplicate handler ever needs
 * to be added.
 */
export type RegulatoryFamily = 'sba' | 'epar' | 'uk_pil' | 'ca_pm' | 'au' | 'eu' | 'jp_pdf' | 'approval_letter';

export type DocumentType =
  | 'label'
  | 'sba'
  | 'epar'
  | 'uk_pil'
  | 'ca_pm'
  | 'au'
  | 'eu'
  | 'jp_pdf'
  | 'approval_letter'
  | 'fda_letter';

export interface AttachedDoc {
  document_id: number;
  title: string | null;
}

export interface AvailableDocument {
  document_type: DocumentType;
  available: boolean | null;   // tri-state
  documents: AttachedDoc[];
}

export interface DrugDocument {
  drug_id: number | null;
  brand_name: string | null;
  generic_name: string | null;
  region: string;
  document_type: DocumentType;
  document_id: number | null;
  title: string | null;
  focused: boolean;
  rank: number | null;
  manufacturer: string | null;
  application_number: string | null;
  marketing_status: string | null;
  approval_date: string | null;
  available_documents: AvailableDocument[];
  has_boxed_warning: boolean | null;
  dosage_form: string | null;
  route: string | null;
}

export interface DrugQAContext {
  drug_id: number;
  set_id: string;
  brand_name: string;
  generic_name?: string | undefined;
}

export interface CompareDrug {
  drug_id: number;
  brand_name?: string | null;
  generic_name?: string | null;
}

export interface DrugCompareContext {
  drugs: CompareDrug[];
}

export interface DrugResultSummary {
  drug_id: number;
  drug_name: string;
  score?: number;
  evidence_snippet?: string;
  evidence_text?: string;
  evidence_section?: string;
  evidence_count?: number;
  // `| null`: the mapping in SearchPage.tsx sends `r.approval_date || null`,
  // not `undefined`, when a result carries no approval date.
  approval_date?: string | null;
  approval_date_precision?: string | null;
  region?: string;
}

export interface SearchResultsContext {
  original_query: string;
  drug_results: DrugResultSummary[];
  selected_drug_ids?: number[];
}

/**
 * Lightweight letter summary shipped in chat context.  Mirrors the
 * backend ``LetterResultSummary`` (``backend/app/schemas/letter_response_schemas.py``).
 * Build from the keyword search response's ``LetterKeywordResultItem``
 * rows — the chat scope must be ``min_length=1`` per the backend
 * Pydantic validator (sending an empty list produces a 422).
 */
export interface LetterResultSummary {
  letter_id: number;
  letter_title?: string | null;
  letter_type: 'warning' | 'untitled' | 'promotional';
  issued_date?: string | null;
  drug_id?: number | null;
  best_snippet: string;
  score: number;
}

export interface LetterSearchResultsContext {
  original_query: string;
  letter_results: LetterResultSummary[];
}

export interface RegulatoryDocumentContext {
  family: RegulatoryFamily;
  document_id: number;
  drug_id?: number | null;
}

export interface FDALetterContext {
  letter_id: number;
}

export type UnifiedChatContext =
  | { mode: 'drug_qa'; drug_qa: DrugQAContext }
  | { mode: 'drug_compare'; drug_compare: DrugCompareContext }
  | { mode: 'search_results'; search_results: SearchResultsContext }
  | { mode: 'search_results_letters'; letter_search_results: LetterSearchResultsContext }
  | { mode: 'regulatory_document'; regulatory_document: RegulatoryDocumentContext }
  | { mode: 'fda_letter'; fda_letter: FDALetterContext };

export interface UseUnifiedChatOptions {
  /**
   * Read at send time. ``null`` = no page context (shell mode); sent as the D9
   * empty search_results shape. A getter (not a value) so the caller can derive
   * the context from state this hook owns (threadId, corpusPinned).
   */
  getContext: () => UnifiedChatContext | null;
  /** Label VERSION the answer should be grounded in. 'origin' pins CEPI's
   *  label tools to each product's ORIGINAL label instead of its current one.
   *
   *  ADMIN-ONLY, and the server is the boundary: a non-admin sending this has
   *  it dropped to current (chat answers from the live label rather than
   *  403-ing mid-conversation). Omitted from the body entirely when unset, so
   *  the default request is unchanged. */
  // Read at send time, like `getContext`: whether the origin toggle applies
  // depends on the effective source, which depends on state this hook owns.
  // ``| undefined`` is required, not noise: the project sets
  // exactOptionalPropertyTypes, so a return type of 'current' | 'origin'
  // would reject an explicit undefined.
  getVersionScope?: () => 'current' | 'origin' | undefined;
  /** US-Drugs@FDA visible: send ``drugs_at_fda: true``. Omitted from the body
   *  when false/unset, so the default request is byte-identical. */
  getDrugsAtFda?: () => boolean;
}

/**
 * Snapshot of an in-flight tool call surfaced to the UI. Populated when
 * the backend emits a ``tool_use`` SSE event (which fires BEFORE the
 * await on the tool dispatch, so banners can show during long waits).
 * Cleared on stream completion in the ``done`` handler.
 */
export interface StreamingToolUse {
  name: string;
  turn: number;
  longRunning: boolean;
  userMessage: string | null;
}

export interface UseUnifiedChatReturn {
  messages: QAMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string;
  streamingStatus: string;
  /**
   * Structured "stage" string from the backend's structured status event
   * (``{stage, message}``). Null when no status has arrived yet or when
   * the server sent a legacy string-only payload.
   */
  streamingStage: string | null;
  /**
   * Current tool invocation snapshot — set from ``tool_use`` SSE events,
   * cleared on stream completion. Used by the UI to show a long-running
   * banner with the tool's pre-canned ``userMessage``.
   */
  streamingToolUse: StreamingToolUse | null;
  streamingCitations: SimpleCitation[];
  askQuestion: (question: string) => Promise<void>;
  cancelStream: () => void;
  /**
   * Server-minted conversation id, present once something has been attached.
   * Sent on every request so the active-result tools can resolve what "those"
   * refers to without the model naming a run.
   */
  threadId: string | null;
  /** Adopt the id the server returned from an attach. */
  setThreadId: (id: string | null) => void;
  /** Shell mode: older turns were dropped to stay within the turn cap. */
  trimmed: boolean;
  /** Shell mode: the user explicitly picked the whole label corpus as scope. Persisted. */
  corpusPinned: boolean;
  /** Set/clear the pin; persists immediately (even mid-stream) under the owning identity. */
  setCorpusPinned: (pinned: boolean) => void;
  /** Shell mode: start a fresh conversation (messages, thread, history, pin). */
  newChat: () => void;
  /** Shell mode: re-send the user turn behind a failed/interrupted LAST reply. */
  retry: (assistantMessageId: string) => void;
  /** Shell mode: record (and persist, via the normal settle effect) that this
   *  message's capability tab has been opened. */
  markOpened: (messageId: string) => void;
}

/** D9: the request shape when there is no page context. */
const NO_CONTEXT: UnifiedChatContext = {
  mode: 'search_results',
  search_results: { original_query: '', drug_results: [], selected_drug_ids: [] },
};

// =============================================================================
// Helpers
// =============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * The identity this tab is acting as right now: signed-in uid + effective org
 * (the ``X-Impersonate-Org`` the backend will actually receive). Read on every
 * render rather than memoised, so an identity change is seen on the next
 * render. Null when signed out — the store treats that as fail-closed.
 */
function currentChatScope(): string | null {
  return buildChatScope(auth.currentUser?.uid, getImpersonationHeaders()['X-Impersonate-Org']);
}

export function useUnifiedChat({ getContext, getVersionScope, getDrugsAtFda }: UseUnifiedChatOptions): UseUnifiedChatReturn {
  // A conversation belongs to the identity that asked (scope). Stored
  // conversations are tenant data — answers are produced under the caller's
  // org — so every read, write and clear below is keyed by it.
  const chatScope = currentChatScope();

  // Restore this identity's conversation BEFORE the first paint, so a
  // re-mount or a reload finds it again. Lazy-init the ref so the snapshot is
  // read once per mount, not on every render.
  const restoredRef = useRef<ShellSnapshot | null | undefined>(undefined);
  if (restoredRef.current === undefined) {
    restoredRef.current = readShell(chatScope);
  }
  const [trimmed, setTrimmed] = useState<boolean>(
    () => restoredRef.current?.trimmed ?? false,
  );

  const [messages, setMessages] = useState<QAMessage[]>(
    () => restoredRef.current?.messages ?? [],
  );
  // The identity the CURRENT ``messages`` belong to. Tracked as state (not a
  // ref) because the persist effect below must never write a conversation
  // under an owner it did not come from: on a change React runs both effects
  // before the re-render, so ``chatScope`` is already the new one while
  // ``messages`` is still the old one. That would mean filing one tenant's
  // conversation under another.
  const [messagesScope, setMessagesScope] = useState<string | null>(chatScope);
  // Conversation identity for attached results. Mirrored into a ref because
  // `askQuestion` is a stable useCallback and must read the CURRENT value, not
  // the one captured when it was created.
  const [threadId, setThreadIdState] = useState<string | null>(
    () => restoredRef.current?.threadId ?? null,
  );
  const threadIdRef = useRef<string | null>(threadId);
  // Raw state; identity-swap restore uses this setter directly so it can never
  // patch the outgoing identity's snapshot.
  const [corpusPinned, setCorpusPinnedState] = useState<boolean>(
    () => restoredRef.current?.corpusPinned ?? false,
  );
  const setThreadId = useCallback((id: string | null) => {
    threadIdRef.current = id;
    setThreadIdState(id);
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingStatus, setStreamingStatus] = useState('');
  // Backend now emits structured status events ({stage, message}); we
  // surface the stage separately so the UI can drive transitions off
  // semantic markers ("cache" / "investigate" / "generate") rather than
  // pattern-matching the user-facing message string.
  const [streamingStage, setStreamingStage] = useState<string | null>(null);
  const [streamingToolUse, setStreamingToolUse] = useState<StreamingToolUse | null>(null);
  const [streamingCitations, setStreamingCitations] = useState<SimpleCitation[]>([]);

  const conversationRef = useRef<Array<{ role: string; content: string }>>(
    restoredRef.current?.conversation ?? [],
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const scopeRef = useRef(chatScope);
  const getContextRef = useRef(getContext);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  // Same ref treatment as `getContext`: askQuestion is a useCallback that must
  // not be rebuilt when the toggle flips, or an in-flight stream's handlers
  // go stale.
  const getVersionScopeRef = useRef(getVersionScope);
  const getDrugsAtFdaRef = useRef(getDrugsAtFda);

  // Keep context ref current on every render (shell mode swaps context freely).
  getContextRef.current = getContext;

  useEffect(() => {
    getVersionScopeRef.current = getVersionScope;
  }, [getVersionScope]);

  useEffect(() => {
    getDrugsAtFdaRef.current = getDrugsAtFda;
  }, [getDrugsAtFda]);

  // Swap conversations when the identity changes.
  //
  // The swap is load-bearing: a new identity must never inherit another user's
  // or another org's conversation. We restore the snapshot belonging to the NEW
  // identity — empty for one never seen (and always empty when signed out).
  useEffect(() => {
    if (scopeRef.current === chatScope) return;
    // An answer still streaming was requested by the PREVIOUS identity.
    // Left running, its completion would push that identity's turn into
    // conversationRef — which now belongs to the new identity and is sent
    // as conversation_history on its next question. Abort it.
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    scopeRef.current = chatScope;
    const restored = readShell(chatScope);
    setTrimmed(restored?.trimmed ?? false);
    setMessages(restored?.messages ?? []);
    conversationRef.current = restored?.conversation ?? [];
    // A thread is bound to ONE conversation. Carrying it across an identity
    // switch would make "those" resolve to a result the new conversation
    // never attached.
    threadIdRef.current = restored?.threadId ?? null;
    setThreadIdState(restored?.threadId ?? null);
    setCorpusPinnedState(restored?.corpusPinned ?? false);
    setMessagesScope(chatScope);
  }, [chatScope]);

  // User-facing setter: also patches storage now, because the persist effect
  // writes only once per in-flight turn and would miss a mid-stream change.
  const setCorpusPinned = useCallback(
    (pinned: boolean) => {
      setCorpusPinnedState(pinned);
      writeShellCorpusPinned(messagesScope, pinned);
    },
    [messagesScope],
  );

  // Persist the conversation so a re-mount or a reload can restore it.
  // Writing under ``messagesScope`` — not the live ``chatScope`` — is what
  // keeps an identity change from filing the outgoing conversation under the
  // incoming identity.
  const inFlightPersistedRef = useRef(false);
  useEffect(() => {
    if (isLoading || isStreaming) {
      // Persist ONCE when the turn starts: the store marks the in-flight
      // reply "Response interrupted", so a reload mid-stream shows it.
      if (!inFlightPersistedRef.current) {
        inFlightPersistedRef.current = true;
        writeShellChat(messagesScope, {
          messages,
          conversation: conversationRef.current,
          threadId: threadIdRef.current,
          trimmed,
          corpusPinned,
        });
      }
      return;
    }
    inFlightPersistedRef.current = false;
    // Turn cap: messages and the history sent to the backend are cut at the
    // same turn boundary. Re-runs once on the trimmed messages and settles.
    const t = trimToRecentTurns(messages, conversationRef.current);
    if (t.trimmed) {
      conversationRef.current = t.conversation;
      setTrimmed(true);
      setMessages(t.messages);
      return;
    }
    writeShellChat(messagesScope, {
      messages,
      conversation: conversationRef.current,
      threadId: threadIdRef.current,
      trimmed,
      corpusPinned,
    });
  // `threadId` is a dep so a thread minted by an attach (no new message) is
  // persisted too; otherwise a reload before the next question loses it.
  }, [messages, messagesScope, isLoading, isStreaming, trimmed, threadId, corpusPinned]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingStatus('');
    setStreamingStage(null);
    setStreamingToolUse(null);
  }, []);

  const newChat = useCallback(() => {
    cancelStream();
    setMessages([]);
    conversationRef.current = [];
    setThreadId(null);
    setTrimmed(false);
    setCorpusPinnedState(false);
    writeShellChat(scopeRef.current, {
      messages: [],
      conversation: [],
      threadId: null,
      trimmed: false,
      corpusPinned: false,
    });
  }, [cancelStream, setThreadId]);

  const askQuestion = useCallback(async (question: string) => {
    if (!question.trim() || isLoading) return;

    // Add user + placeholder assistant messages
    const userMsg: QAMessage = {
      id: generateId(),
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };
    const assistantId = generateId();
    const assistantMsg: QAMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setIsLoading(true);
    setIsStreaming(false);
    setStreamingContent('');
    setStreamingStatus('');
    setStreamingStage(null);
    setStreamingToolUse(null);
    setStreamingCitations([]);

    abortControllerRef.current = new AbortController();
    let accumulatedContent = '';
    let accumulatedCitations: SimpleCitation[] = [];

    try {
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken(false) : null;

      // Build request body
      const ctx = getContextRef.current() ?? NO_CONTEXT;
      const body: Record<string, unknown> = {
        question: question.trim(),
        mode: ctx.mode,
      };
      // Only present once this conversation has durable state. Omitted
      // otherwise so an ordinary chat request is byte-for-byte unchanged.
      if (threadIdRef.current) body['thread_id'] = threadIdRef.current;
      // Only sent when explicitly set, so a normal chat request is byte-for-byte
      // what it was before this feature.
      const versionScope = getVersionScopeRef.current?.();
      if (versionScope) body['version_scope'] = versionScope;
      // Only when US-Drugs@FDA is visible; otherwise the body is unchanged.
      if (getDrugsAtFdaRef.current?.()) body['drugs_at_fda'] = true;

      // Add mode-specific context. The structured-output cutover retired
      // selected_version_ids — CEPI now reads any version by ordinal at the
      // tool layer, so the drug_qa context passes through unmodified.
      if (ctx.mode === 'drug_qa') body['drug_qa'] = ctx.drug_qa;
      else if (ctx.mode === 'drug_compare') body['drug_compare'] = ctx.drug_compare;
      else if (ctx.mode === 'search_results') body['search_results'] = ctx.search_results;
      else if (ctx.mode === 'search_results_letters') body['letter_search_results'] = ctx.letter_search_results;
      else if (ctx.mode === 'regulatory_document') body['regulatory_document'] = ctx.regulatory_document;
      else if (ctx.mode === 'fda_letter') body['fda_letter'] = ctx.fda_letter;

      // Add conversation history
      if (conversationRef.current.length > 0) {
        body['conversation_history'] = conversationRef.current;
      }

      const response = await fetch(`${STREAMING_API_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      setIsStreaming(true);
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events (split on double-newline)
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          if (!part.trim()) continue;

          let eventType = '';
          const dataLines: string[] = [];

          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              dataLines.push(line.slice(6));
            }
          }

          if (!eventType || dataLines.length === 0) continue;
          const data = dataLines.join('\n');

          switch (eventType) {
            case 'status': {
              // Backend now emits structured payload: { stage, message }.
              // Fall back to raw string for legacy compatibility — pre-B3
              // callers (and the cache-hit replay path on older deploys)
              // emit a plain-string data line.
              try {
                const parsed = JSON.parse(data);
                if (parsed && typeof parsed === 'object' && 'message' in parsed) {
                  setStreamingStatus(parsed.message ?? '');
                  setStreamingStage(parsed.stage ?? null);
                } else {
                  setStreamingStatus(data);
                  setStreamingStage(null);
                }
              } catch {
                setStreamingStatus(data);
                setStreamingStage(null);
              }
              setMessages(prev =>
                prev.map(m => m.id === assistantId
                  ? { ...m, content: accumulatedContent, isLoading: true }
                  : m
                )
              );
              break;
            }

            case 'token':
              // Buffered-emit (CEPI structured-output): the full answer
              // arrives as a SINGLE token event, then citations +
              // verification, then ``done``. We accumulate into the
              // streaming buffer ONLY and commit to the assistant message
              // at ``done`` — this keeps the in-flight answer rendering in
              // EXACTLY ONE place (StreamingMessage) during the
              // token→done window, instead of double-rendering in both
              // StreamingMessage and MessageBubble. The message stays
              // ``isLoading`` (LoadingBubble) until ``done`` swaps in the
              // final content and drops ``isStreaming``.
              accumulatedContent += data;
              setStreamingContent(accumulatedContent);
              break;

            case 'tool_use': {
              // B3 payload: { tool, turn, long_running, user_message }.
              // Surface the full snapshot so the UI can show a long-running
              // banner with the tool's pre-canned userMessage during the
              // wait — backend emits tool_use BEFORE awaiting the call.
              try {
                const parsed = JSON.parse(data);
                setStreamingToolUse({
                  name: parsed.tool,
                  turn: parsed.turn,
                  longRunning: !!parsed.long_running,
                  userMessage: parsed.user_message ?? null,
                });
              } catch {
                // Defensive: ignore malformed payloads.
              }
              break;
            }

            case 'citation':
              try {
                const citation = JSON.parse(data) as SimpleCitation;
                accumulatedCitations = [...accumulatedCitations, citation];
                setStreamingCitations([...accumulatedCitations]);
                setMessages(prev =>
                  prev.map(m => m.id === assistantId
                    ? { ...m, citations: [...accumulatedCitations] }
                    : m
                  )
                );
              } catch {
                // ignore
              }
              break;

            case 'verification': {
              // CEPI structured-output verifier verdicts. Payload is a JSON
              // array of { segment_index, status, reason }. Arrives AFTER
              // the buffered ``token`` answer and citations, BEFORE ``done``
              // (only when the backend's segment verifier is enabled). We
              // FLAG, never strip: stash the verdicts on the in-flight
              // assistant message so MessageBubble can surface a "could not
              // verify" affordance — the answer text is left untouched.
              try {
                const parsed = JSON.parse(data);
                if (Array.isArray(parsed)) {
                  const verdicts = parsed as SegmentVerification[];
                  setMessages(prev =>
                    prev.map(m => m.id === assistantId
                      ? { ...m, verification: verdicts }
                      : m
                    )
                  );
                }
              } catch {
                // Defensive: ignore malformed verification payloads — the
                // answer + citations already rendered; a bad verdict blob
                // must not blank the message.
              }
              break;
            }

            case 'done': {
              let finalContent = accumulatedContent;
              let answerStatus: QAMessage['answer_status'];
              let capabilityAction: CapabilityAction | undefined;
              try {
                const doneData = JSON.parse(data);
                if (doneData.citations) {
                  accumulatedCitations = doneData.citations;
                }
                // WHY there is (or isn't) an answer. The two failure statuses
                // arrive over a 200 with a human-readable body, so without
                // this a non-answer renders exactly like an answer. Only the
                // three known values are accepted — an unrecognised one is
                // dropped rather than passed through to the renderer.
                if (
                  doneData.answer_status === 'ok'
                  || doneData.answer_status === 'evidence_incomplete'
                  || doneData.answer_status === 'no_answer'
                ) {
                  answerStatus = doneData.answer_status;
                }
                // Capability handoff. Validation lives in its own module so
                // it is testable: the version inlined here re-imposed the
                // backend's retired gates (investigator-only, non-empty
                // cohort) and silently dropped every Cogni, Keyword and
                // discovery-mode card before it could render.
                capabilityAction = parseCapabilityAction(doneData) ?? undefined;
                // Backend may post-process the streamed answer (e.g. linkify
                // bracket citations like "[Section 8]" into anchor tags).
                // When it does, it ships the rewritten HTML in final_html
                // and we swap it in here so links become clickable.
                if (typeof doneData.final_html === 'string' && doneData.final_html.length > 0) {
                  finalContent = doneData.final_html;
                }
              } catch {
                // done data might not be JSON
              }
              setMessages(prev =>
                prev.map(m => m.id === assistantId
                  ? {
                      ...m,
                      content: finalContent,
                      isLoading: false,
                      citations: accumulatedCitations,
                      ...(answerStatus ? { answer_status: answerStatus } : {}),
                      ...(capabilityAction
                        ? { capability_action: capabilityAction } : {}),
                    }
                  : m
                )
              );
              // Stream complete — commit the buffered answer to the message
              // and flip ``isStreaming`` off in the SAME render batch so the
              // hand-off from StreamingMessage to MessageBubble is atomic:
              // the answer never renders in both at once and there's no
              // flicker/gap. (The ``finally`` block also clears these; doing
              // it here keeps the swap batched with the message commit.)
              setIsStreaming(false);
              setStreamingContent('');
              // Stream complete — clear stage + tool-use snapshot so any
              // long-running banner the UI was showing comes down.
              setStreamingStage(null);
              setStreamingToolUse(null);
              break;
            }

            case 'error':
              try {
                const errData = JSON.parse(data);
                setMessages(prev =>
                  prev.map(m => m.id === assistantId
                    ? { ...m, content: '', isLoading: false, error: errData.message || 'Error' }
                    : m
                  )
                );
              } catch {
                setMessages(prev =>
                  prev.map(m => m.id === assistantId
                    ? { ...m, content: '', isLoading: false, error: data }
                    : m
                  )
                );
              }
              // Flip streaming off + clear the buffer in the SAME batch as the
              // error commit, so StreamingMessage disappears atomically with the
              // error bubble appearing (no window where both render).
              setIsStreaming(false);
              setStreamingContent('');
              break;
          }
        }
      }

      // Update conversation history
      if (accumulatedContent) {
        conversationRef.current.push(
          { role: 'user', content: question.trim() },
          { role: 'assistant', content: accumulatedContent },
        );
        // Stamp the pair's owner so turn-trimming counts exactly what was pushed.
        setMessages(prev =>
          prev.map(m => m.id === assistantId ? { ...m, inHistory: true } : m),
        );
      }

    } catch (e: any) {
      if (e.name === 'AbortError') return;

      const rawError = e.message || 'An error occurred';
      let userMessage = 'Sorry, an error occurred. Please try again.';
      const lower = rawError.toLowerCase();
      if (lower.includes('rate') || lower.includes('429')) {
        userMessage = 'The AI service is busy. Please wait a moment and try again.';
      } else if (lower.includes('timeout') || lower.includes('timed out')) {
        userMessage = 'The request took too long. Try a simpler question.';
      }

      setMessages(prev =>
        prev.map(m => m.id === assistantId
          ? { ...m, isLoading: false, content: userMessage, error: rawError }
          : m
        )
      );
      setIsStreaming(false);
      setStreamingContent('');
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingContent('');
      setStreamingStatus('');
      setStreamingStage(null);
      setStreamingToolUse(null);
    }
  }, [isLoading]);

  const retry = useCallback((assistantMessageId: string) => {
    if (isLoading) return;
    const msgs = messagesRef.current;
    const idx = msgs.findIndex((m) => m.id === assistantMessageId);
    const target = msgs[idx];
    // Only the LAST turn, and only a failed/interrupted reply.
    if (!target || idx < 1 || idx !== msgs.length - 1 || msgs[idx - 1]?.role !== 'user') return;
    if (!target.error && !target.interrupted) return;
    const userMsg = msgs[idx - 1]!;
    // An errored-after-tokens turn was already pushed to history; drop its pair
    // so the re-sent question is not duplicated.
    if (target.inHistory) conversationRef.current = conversationRef.current.slice(0, -2);
    setMessages((prev) => prev.filter((m) => m.id !== target.id && m.id !== userMsg.id));
    void askQuestion(userMsg.content);
  }, [askQuestion, isLoading]);

  const markOpened = useCallback((messageId: string) => {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, opened: true } : m)));
  }, []);

  return {
    messages,
    isLoading,
    isStreaming,
    streamingContent,
    streamingStatus,
    streamingStage,
    streamingToolUse,
    streamingCitations,
    askQuestion,
    cancelStream,
    threadId,
    setThreadId,
    trimmed,
    corpusPinned,
    setCorpusPinned,
    newChat,
    retry,
    markOpened,
  };
}

export default useUnifiedChat;
